import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Button,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect, useState, useRef } from "react";
import DatePickerRangeComponent from "../../components/DatePickerRange";
import styled from "./style.module.css";
import { useQuery } from "react-query";
import { getRevenueByTherapist } from "../../api/dailyReport";
import usePaginate from "../../hooks/usePaginate";
import TablePaginationLayout from "../../layouts/TablePagination";
import { formatNumberWithUnit, getCountOrderNominatation } from "../../utils";
import { STALE_TIME_DEFAULT } from "../../constants";

// Function to calculate the correct working date
// If current time is between 0:00-5:00, it's still considered part of the previous day's work shift
const getWorkingDate = () => {
  const now = dayjs();
  const currentHour = now.hour();
  
  // If current time is between 0:00-5:00, return previous day
  if (currentHour >= 0 && currentHour < 5) {
    return now.subtract(1, "day");
  }
  
  return now;
};

const styleTableHead = {
  backgroundColor: "#000",
  color: "#fff",
  minWidth: "100px",
  textAlign: "center",
  verticalAlign: "middle",
  padding: "auto",
  paddingLeft: 0,
  paddingRight: 0,
};

const styleTableBody = {
  ...styleTableHead,
  color: "#000",
  backgroundColor: "transparent",
};

const MIN_COL_WIDTH = 100;

const RevenueTherapist = ({ dataNominatation }) => {
  const [startDate, setStartDate] = useState(getWorkingDate());
  const [endDate, setEndDate] = useState(getWorkingDate());
  const [searchName, setSearchName] = useState("");
  const [showAllData, setShowAllData] = useState(false);
  const { page, perPage, total, changePage, setTotal, changePerPage } =
    usePaginate(0, 10);

  const [data, setData] = useState([]);

  const containerRef = useRef();
  const [containerWidth, setContainerWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleShowAllData = () => {
    setShowAllData(true);
  };
  
  useEffect(() => {
    if (showAllData && page > 0) {
      setShowAllData(false);
    }
  }, [page]);

  const { data: dataTherapist } = useQuery(
    ["list_revenue_therapist", startDate, endDate, page, searchName, showAllData],
    () => {
      console.log(showAllData, total, page);
      
      return getRevenueByTherapist({
        start_date: startDate.format("YYYY-MM-DD"),
        end_date: endDate.format("YYYY-MM-DD"),
        per_page: showAllData ? total : 10,
        page: showAllData ? 1 : page + 1,
        keyword: searchName,
      });
    },
    {
      keepPreviousData: true,
      staleTime: STALE_TIME_DEFAULT,
    }
  );

  useEffect(() => {
    if (dataTherapist) {
      setData(dataTherapist.data.data);
      setTotal(dataTherapist.data.total);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTherapist]);

  // Tính toán số cột động
  const totalColumns = 3 + dataNominatation.length + 1;
  const maxColumns = Math.floor(containerWidth / MIN_COL_WIDTH);
  const isManyColumns = totalColumns > maxColumns;
  const tableProps = isManyColumns
    ? { sx: { minWidth: totalColumns * MIN_COL_WIDTH, tableLayout: "fixed" } }
    : { sx: { width: "100%", tableLayout: "auto" } };
  const cellProps = isManyColumns
    ? { sx: { minWidth: `${MIN_COL_WIDTH}px` } }
    : {};

  return (
    <Container maxWidth="xl" ref={containerRef}>
      <div
        className={`${styled.dateRangeWrapper}`}
        style={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "center",
          alignItems: "center",
          maxWidth: "1200px",
          margin: "0 auto",
          gap: { xs: "16px", md: "32px" },
          padding: "16px",
        }}
      >
        <Typography
          sx={{
            fontSize: {
              md: 20,
              xs: 10,
            },
            letterSpacing: {
              md: "3px",
              xs: 0,
            },
            color: "#fff",
            textDecoration: "none",
          }}
        >
          セラピスト別詳細
        </Typography>
        <Box
          className={`${styled.dateRangeContainer}`}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: "16px",
            alignItems: "center",
            justifyContent: "center",
            maxWidth: { sm: "800px" },
          }}
        >
          <div style={{ display: "flex" }}>
            <DatePickerRangeComponent
              onDateChange={(start, end) => handleDateChange(start, end)}
              startDate={startDate}
              endDate={endDate}
            />
          </div>
          <Box sx={{ width: { xs: "100%", sm: "300px" } }}>
            <TextField
              size="medium"
              label="セラピスト名"
              placeholder="セラピスト名で検索"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              fullWidth
            />
          </Box>
        </Box>
      </div>

      <Container
        // maxWidth="xl"
        sx={{
          padding: "0px !important",
        }}
      >
        <Box sx={{ marginTop: "20px" }}>
          <Paper
            sx={{
              backgroundColor: "rgb(255, 255, 255)",
              color: "rgba(0, 0, 0, 0.87)",
              transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
              borderRadius: "4px",
              boxShadow:
                "rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px",
              width: "100%",
              marginBottom: "16px",
              overflow: "auto",
            }}
          >
            <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
              <Table {...tableProps}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...styleTableHead, ...cellProps.sx }}>
                      セラピスト
                    </TableCell>
                    <TableCell sx={{ ...styleTableHead, ...cellProps.sx }}>
                      出勤数
                    </TableCell>
                    <TableCell sx={{ ...styleTableHead, ...cellProps.sx }}>
                      総接客数
                    </TableCell>
                    {dataNominatation.map((item) => (
                      <TableCell
                        key={item.id}
                        sx={{ ...styleTableHead, ...cellProps.sx }}
                      >
                        {item.name}
                      </TableCell>
                    ))}
                    <TableCell sx={{ ...styleTableHead, ...cellProps.sx }}>
                      本指名率
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((item, index) => (
                    <TableRow
                      key={item.therapist_id}
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "#fff",
                      }}
                    >
                      <TableCell sx={{ ...styleTableBody, ...cellProps.sx }}>
                        {item.therapist_name}
                      </TableCell>
                      <TableCell sx={{ ...styleTableBody, ...cellProps.sx }}>
                        {formatNumberWithUnit(item.count_working_days, "日")}
                      </TableCell>
                      <TableCell sx={{ ...styleTableBody, ...cellProps.sx }}>
                        {formatNumberWithUnit(item.total_customer, "人")}
                      </TableCell>
                      {dataNominatation.map((itemNomination) => (
                        <TableCell
                          key={itemNomination.id}
                          sx={{ ...styleTableBody, ...cellProps.sx }}
                        >
                          {getCountOrderNominatation(
                            item.nominations || [],
                            itemNomination.id
                          )}
                        </TableCell>
                      ))}
                      <TableCell sx={{ ...styleTableBody, ...cellProps.sx }}>
                        {item.main_nomination_rate}%
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!data || data.length === 0) && (
                    <TableRow>
                      <TableCell
                        colSpan={dataNominatation.length + 4}
                        align="center"
                        sx={{ width: "100%" }}
                      >
                        <Typography>該当データがありません。</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", justifyContent: "end", alignItems: "center", gap: 2, padding: "16px", borderTop: "1px solid #e0e0e0" }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleShowAllData}
              >
                全件表示
              </Button>
              <TablePaginationLayout
                rowsPerPage={perPage}
                total={total}
                page={page}
                rowsPerPageOptions={[10]}
                onPageChange={changePage}
                handleChangeRowsPerPage={changePerPage}
              />
            </Box>
          </Paper>
        </Box>
      </Container>
    </Container>
  );
};

export default RevenueTherapist;
